// InvoiceModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Button, Spin, Alert } from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import './InvoiceModal.css';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import RatesApi from '../../common/Apis/RatesApi';

const InvoiceModal = ({ open, onCancel, tenant }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tenantRates, setTenantRates] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);

  // Fetch tenant rates when modal opens
  useEffect(() => {
    if (open && tenant?.id) {
      fetchTenantRates();
    }
  }, [open, tenant?.id]);

  const fetchTenantRates = async () => {
    if (!tenant?.id) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await RatesApi.GetTenantRatesByTenantID(tenant?.id);
      setTenantRates(response);
      initializeInvoiceData(response);
    } catch (err) {
      console.error('Error fetching tenant rates:', err);
      setError(err.message || 'Failed to fetch tenant rates');
      // Initialize with default data if API fails
      initializeInvoiceData(null);
    } finally {
      setLoading(false);
    }
  };

  const calculateVATBreakdown = (subtotal) => {
    const currentVATRate = 0.23; // 23% current rate
    const vatAmount = subtotal * currentVATRate;
    const totalWithVAT = subtotal + vatAmount;
    
    // Calculate historical VAT amounts (for display purposes only)
    const historicalVAT = [
      { rate: "0%", rateValue: 0.00, amount: 0.00 },
      { rate: "13.5%", rateValue: 0.135, amount: subtotal * 0.135 },
      { rate: "23%", rateValue: currentVATRate, amount: vatAmount }
    ];
    
    return {
      currentVATRate: currentVATRate,
      vatAmount: vatAmount,
      totalWithVAT: totalWithVAT,
      historicalVAT: historicalVAT
    };
  };

  const initializeInvoiceData = (rates) => {
    const currentDate = new Date();
    const dueDate = new Date(currentDate);
    dueDate.setDate(dueDate.getDate() + 30);

    // Get the active invoice (with invoiceDate and invoiceAmount)
    const rateData = rates?.payloadDto?.find(rate => rate.invoiceDate && rate.invoiceAmount) || rates?.payloadDto?.[0];
    const tenantData = rateData?.tenantDTO || tenant;

    const baseInvoiceData = {
      invoiceNo: `INV-${currentDate.getFullYear()}${String(currentDate.getMonth() + 1).padStart(2, '0')}${String(currentDate.getDate()).padStart(2, '0')}`,
      invoiceDate: rateData?.invoiceDate || currentDate.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      company: {
        name: "IVA Food Management Service Pvt Ltd", //Address: 50 Grove Park, Mountmellick Co.Laois Ireland R32FX56. Phone : 0858072842 VAT number is 4038942GH
        address: "1 50 Grove Park, Mountmellick Co.Laois Ireland R32FX56",
        email: "info@foodflowcompany.ie",
        phone: "0858072842",
        website: "www.foodflowcompany.ie",
        vatNumber: "4038942GH"
      },
      client: {
        name: tenantData?.tenantName,
        code: tenantData?.tenantCode,
        contactPerson: tenantData?.contactPerson,
        email: tenantData?.contactEmail,
        phone: tenantData?.contactPhone,
        address: tenantData?.contactAddress
      },
      invoicePeriod: `${tenantData?.statusStartDate || currentDate.toISOString().split('T')[0]} → ${tenantData?.statusEndDate || dueDate.toISOString().split('T')[0]}`,
      status: tenantData?.paymentStatus || "PAID",
      paymentMethod: "Bank Transfer / Online Payment"
    };

    if (rateData) {
      const agreedRate = rateData.agreedRate || 0;
      const invoiceAmount = rateData.invoiceAmount || 0;
      const billingCycle = rateData.billingCycle || tenantData?.billingCycle || "ANNUALLY";
      const currency = tenantData?.currency || "USD";
      
      // Get sub-tenant data
      const subTenantList = rateData.subTenantList || [];
      const subTenantTotalRate = rateData.subTenantTotalRate || 0;
      
      // Create line items
      const items = [];
      
      // Sub-tenant items (each as separate line)
      subTenantList.forEach((subTenant, index) => {
        items.push({
          description: `Sub-tenant: ${subTenant.subTenantName} (${subTenant.subTenantCode})`,
          billingCycle: billingCycle === "MONTHLY" ? "Monthly" : "Annual",
          rate: subTenant.subTenantRate?.toFixed(2) || "0.00",
          amount: subTenant.subTenantRate?.toFixed(2) || "0.00",
          type: 'subtenant',
          subTenantCode: subTenant.subTenantCode
        });
      });

      // Calculate VAT breakdown
      const vatCalculations = calculateVATBreakdown(invoiceAmount);

      const calculatedData = {
        ...baseInvoiceData,
        items: items,
        subtotal: invoiceAmount.toFixed(2),
        tax: 0.00,
        vatRate: (vatCalculations.currentVATRate * 100).toFixed(1), // 23.0%
        vatAmount: vatCalculations.vatAmount.toFixed(2),
        totalWithVAT: vatCalculations.totalWithVAT.toFixed(2),
        totalDue: vatCalculations.totalWithVAT.toFixed(2),
        currency: currency,
        tenantRate: (agreedRate - subTenantTotalRate).toFixed(2),
        subTenantTotalRate: subTenantTotalRate.toFixed(2),
        agreedRate: agreedRate.toFixed(2),
        historicalVAT: vatCalculations.historicalVAT
      };

      setInvoiceData(calculatedData);
    } else {
      // Use default/fallback data
      const billingCycle = tenant?.billingCycle || "ANNUALLY";
      const defaultRate = 200.00;
      const currency = tenant?.currency || "USD";

      // Calculate VAT for default data
      const vatCalculations = calculateVATBreakdown(defaultRate);

      const defaultData = {
        ...baseInvoiceData,
        items: [
          {
            description: "Food Safety Management Subscription",
            billingCycle: billingCycle === "MONTHLY" ? "Monthly" : "Annual",
            rate: defaultRate.toFixed(2),
            amount: defaultRate.toFixed(2),
            type: 'main'
          }
        ],
        subtotal: defaultRate.toFixed(2),
        tax: 0.00,
        vatRate: (vatCalculations.currentVATRate * 100).toFixed(1), // 23.0%
        vatAmount: vatCalculations.vatAmount.toFixed(2),
        totalWithVAT: vatCalculations.totalWithVAT.toFixed(2),
        totalDue: vatCalculations.totalWithVAT.toFixed(2),
        currency: currency,
        tenantRate: defaultRate.toFixed(2),
        subTenantTotalRate: "0.00",
        agreedRate: defaultRate.toFixed(2),
        historicalVAT: vatCalculations.historicalVAT
      };

      setInvoiceData(defaultData);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = async () => {
    const element = document.getElementById('invoice-content');
    
    if (!element) {
      console.error('Invoice content not found');
      return;
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`invoice-${invoiceData?.invoiceNo}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    }
  };

  if (loading) {
    return (
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={400}
        className="invoice-modal"
      >
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px' }}>Loading invoice data...</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onCancel}
      footer={null}
      width="100%"
      style={{ maxWidth: '90%' }}
      className="invoice-modal"
      title={
        <div className="modal-header">
          <span>Invoice Preview</span>
          <div className="modal-actions">
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>
              Print
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined style={{ color: "#fff" }} />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
          </div>
        </div>
      }
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: '16px' }}
        />
      )}

      {invoiceData && (
        <div className="invoice-container" id="invoice-content">
          {/* Page 1 */}
          <div className="invoice-page">
            <div className="invoice-header">
              <h1>IVA FOOD MANAGEMENT SERVICE</h1>
              <h2>INVOICE</h2>
              <div className="company-vat">
                <p>VAT No: {invoiceData.company.vatNumber}</p>
              </div>
            </div>

            <div className="invoice-meta">
              <table className="meta-table">
                <tbody>
                  <tr>
                    <td><strong>Invoice No:</strong></td>
                    <td>{invoiceData.invoiceNo}</td>
                  </tr>
                  <tr>
                    <td><strong>Invoice Date:</strong></td>
                    <td>{invoiceData.invoiceDate}</td>
                  </tr>
                  <tr>
                    <td><strong>Due Date:</strong></td>
                    <td>{invoiceData.dueDate}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="billed-to">
              <h3>Billed To</h3>
              <div className="client-info">
                <p><strong>Tenant Name:</strong> {invoiceData.client.name}</p>
                <p><strong>Tenant Code:</strong> {invoiceData.client.code}</p>
                <p><strong>Contact Person:</strong> {invoiceData.client.contactPerson}</p>
                <p><strong>Email:</strong> {invoiceData.client.email}</p>
                <p><strong>Phone:</strong> {invoiceData.client.phone}</p>
                <p><strong>Address:</strong> {invoiceData.client.address}</p>
              </div>
            </div>

            <hr />

            <div className="invoice-details">
              <h3>Invoice Details</h3>
              <table className="details-table">
                <thead>
                  <tr>
                    <th style={{ width: '40%' }}>Description</th>
                    <th style={{ width: '15%' }}>Billing Cycle</th>
                    <th style={{ width: '20%' }}>Rate ({invoiceData.currency})</th>
                    <th style={{ width: '25%' }}>Amount ({invoiceData.currency})</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Main Tenant Line */}
                  {invoiceData.items.filter(item => item.type === 'main').map((item, index) => (
                    <tr key={`main-${index}`} className="main-tenant-row">
                      <td>
                        <div className="item-description">
                          <div className="item-title">{item.description}</div>
                          <div className="tenant-info">
                            <span className="tenant-code">[{invoiceData.client.code}]</span>
                          </div>
                        </div>
                      </td>
                      <td>{item.billingCycle}</td>
                      <td>{invoiceData.currency} {item.rate}</td>
                      <td>{invoiceData.currency} {item.amount}</td>
                    </tr>
                  ))}
                  
                  {/* Sub-tenant Lines */}
                  {invoiceData.items.filter(item => item.type === 'subtenant').map((item, index) => (
                    <tr key={`subtenant-${index}`} className="subtenant-row">
                      <td>
                        <div className="item-description">
                          <div className="item-title">{item.description}</div>
                          <div className="subtenant-info">
                            <span className="subtenant-code">[{item.subTenantCode}]</span>
                          </div>
                        </div>
                      </td>
                      <td>{item.billingCycle}</td>
                      <td>{invoiceData.currency} {item.rate}</td>
                      <td>{invoiceData.currency} {item.amount}</td>
                    </tr>
                  ))}
                  
                  {/* Summary Row */}
                  <tr className="summary-row">
                    <td colSpan="2">
                      <strong>Total Rate Breakdown:</strong>
                    </td>
                    <td colSpan="2">
                      <div className="rate-breakdown">
                        {/* <div>Main Tenant: <strong>{invoiceData.currency} {invoiceData.tenantRate}</strong></div> */}
                        {parseFloat(invoiceData.subTenantTotalRate) > 0 && (
                          <div>Shops / Stores Total: <strong>{invoiceData.currency} {invoiceData.subTenantTotalRate}</strong></div>
                        )}
                        <div>Agreed Total Rate: <strong>{invoiceData.currency} {invoiceData.agreedRate}</strong></div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <div className="invoice-period">
                <p><strong>Invoice Period:</strong> {invoiceData.invoicePeriod}</p>
                <p><strong>Status:</strong> {invoiceData.status}</p>
                <p><strong>Payment Method:</strong> {invoiceData.paymentMethod}</p>
              </div>
            </div>

            <div className="vat-summary-section">
              <h3>VAT Summary</h3>
              
              {/* VAT Details Table */}
              <div className="vat-details-table">
                <table className="vat-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Amount ({invoiceData.currency})</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* <tr>
                      <td>Monthly Food Flow Subscription (Level 1)</td>
                      <td></td>
                    </tr> */}
                    {/* <tr className="currency-row">
                      <td><strong>Currency</strong></td>
                      <td><strong>{invoiceData.currency}</strong></td>
                    </tr> */}
                    <tr>
                      <td>Total Net</td>
                      <td>{invoiceData.subtotal}</td>
                    </tr>
                    <tr>
                      <td>VAT @ {invoiceData.vatRate}%</td>
                      <td>{invoiceData.vatAmount}</td>
                    </tr>
                    <tr className="gross-total-row">
                      <td><strong>Gross</strong></td>
                      <td><strong>{invoiceData.currency} {invoiceData.totalWithVAT}</strong></td>
                    </tr>
                  </tbody>
                </table>

                {/* VAT Analysis Table */}
                <div className="vat-analysis-section">
                  <h4>VAT Analysis</h4>
                  <table className="vat-analysis-table">
                    <thead>
                      <tr>
                        <th>Rate</th>
                        <th>Amount ({invoiceData.currency})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoiceData.historicalVAT.map((vat, index) => (
                        <tr key={index}>
                          <td>{vat.rate}</td>
                          <td>{parseFloat(vat.amount).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* VAT Amount Section */}
                <div className="vat-amount-section">
                  <h4>VAT Amount</h4>
                  <div className="vat-amount-display">
                    <div className="vat-amount-label">Total VAT Applicable:</div>
                    <div className="vat-amount-value">
                      {invoiceData.currency} {invoiceData.vatAmount}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="summary">
              <table className="summary-table">
                <tbody>
                  <tr>
                    <td>Subtotal (Excluding VAT)</td>
                    <td>{invoiceData.currency} {invoiceData.subtotal}</td>
                  </tr>
                  <tr>
                    <td>Tax (0%)</td>
                    <td>{invoiceData.currency} {invoiceData.tax}</td>
                  </tr>
                  <tr>
                    <td>VAT @ {invoiceData.vatRate}%</td>
                    <td>{invoiceData.currency} {invoiceData.vatAmount}</td>
                  </tr>
                  <tr className="total-due">
                    <td><strong>Total Due (Including VAT)</strong></td>
                    <td><strong>{invoiceData.currency} {invoiceData.totalDue}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="notes">
              <h4>Notes:</h4>
              <ul>
                <li>Thank you for choosing IVA Food Management Service.</li>
                <li>Please make the payment within 30 days of the invoice date to avoid service interruptions.</li>
                <li>For payment inquiries, contact us at billing@ivafoodservice.com.</li>
              </ul>
            </div>

            {/* <div className="vat-footer">
              <p><strong>VAT Registered Company</strong></p>
              <p>VAT No: {invoiceData.company.vatNumber}</p>
            </div> */}

            <div className="company-footer">
              <p><strong>{invoiceData.company.name}</strong></p>
              <p>Address: {invoiceData.company.address}</p>
              <p>Email: {invoiceData.company.email} | Phone: {invoiceData.company.phone}</p>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InvoiceModal;